import { Logger, Utils } from "neo-common";

export type ActionsTypes = { [action: string]: boolean };

/**
 * @type BatchUpdatesFunction - predicate function where is able to do multiple batched actions changes and
 * only after on updates ends, event 'onChange' is triggered once!
 * @returns true if where was any changes, false if there was not changes (and thus no 'onChange' is called on end)
 */
export type BatchUpdatesFunction = (actions: IActionSet) => boolean | void;

export interface IActionSet {
    changeActionState(action: string, value: boolean): boolean;
}

/**
 * @class ActionsData - List of actions and its states.
 */
export class ActionSet implements IActionSet {
    private _setName: string;
    private _owner: string;
    private _actions: ActionsTypes = {};
    private _batchUpdatesActive: boolean;
    private _onChange: Function;
    private _readonly: boolean;

    constructor(setName: string, owner: string) {
        this._setName = setName;
        this._owner = owner;
        this._batchUpdatesActive = false;
        this._readonly = false;

        this.registerActions = this.registerActions.bind(this);
        this.registerAction = this.registerAction.bind(this);
        this.changeActionState = this.changeActionState.bind(this);
        this.getActionState = this.getActionState.bind(this);
        this.getAllActions = this.getAllActions.bind(this);
        this.batchUpdates = this.batchUpdates.bind(this);
        this.disableAllActions = this.disableAllActions.bind(this);
        this.invokeOnChange = this.invokeOnChange.bind(this);
    }

    public get onChange(): Function {
        return this._onChange;
    }

    public set onChange(value: Function) {
        this._onChange = value;
    }

    private log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup(this._owner, `[${this._setName}] ` + s);
        }
    }

    private logError(s: string, e?: Error): void {
        if (DEBUG) {
            Logger.errorGroup(this._owner, e, `[${this._setName}] ` + s);
        }
    }

    private invokeOnChange(): void {
        if (this._onChange && !this._batchUpdatesActive) {
            try {
                this.onChange();
            } catch (e) {
                if (DEBUG) {
                    this.logError("invokeOnChange", e);
                }
            }
        }
    }

    private checkForReadonly(): void {
        if (this._readonly) {
            throw new Error(`ActionSet "${this._owner}" [${this._setName}] is readonly!`);
        }
    }

    private beginBatchUpdates(): void {
        this.checkForReadonly();

        if (this._batchUpdatesActive) {
            throw new Error(`"${this._owner}" [${this._setName}] Other batch updates is in pending state!`);
        }

        this._batchUpdatesActive = true;
    }

    private endBatchUpdates(hasChanges: boolean): void {
        this.checkForReadonly();

        this._batchUpdatesActive = false;

        // when return is null or undefined we assume that something HAS changed (return bool was forgotten?)
        if (Utils.isNullOrUndefined(hasChanges)) {
            hasChanges = true;
        }

        if (hasChanges) {
            this.invokeOnChange();
        }
    }

    public batchUpdates(updateFn: BatchUpdatesFunction): void {
        this.beginBatchUpdates();

        var hasChanges: boolean;
        try {
            hasChanges = updateFn(this) as boolean;
        } finally {
            this.endBatchUpdates(hasChanges);
        }
    }

    /**
     * @method registerActions - registers multiple actions at once, on end event 'onChange' is triggered.
     * @param actions - list of actions with its initial state value.
     */
    public registerActions(actions: ActionsTypes): void {
        this.beginBatchUpdates();

        try {
            for (var key in actions) {
                if (actions.hasOwnProperty(key)) {
                    this.registerAction(key, actions[key]);
                }
            }
        } finally {
            this.endBatchUpdates(true);
        }
    }

    /**
     * Register game action for specific controller and add him default value.
     * @method registerAction
     * @param action string name of action
     * @param value boolean default value false
     * @return true if successfully registered, otherwise returns false
     */
    private registerAction(action: string, value: boolean = false): boolean {
        this.checkForReadonly();

        var notExist = Utils.isNullOrUndefined(this._actions[action]);
        if (notExist) {
            this._actions[action] = value;

            this.invokeOnChange();
        } else {
            if (DEBUG) {
                this.log(
                    `[changeActionState] Could not register action '${action}', action was already registered!` +
                        ` all actions: ${JSON.stringify(this._actions)}`,
                );
            }
        }

        return notExist;
    }

    /**
     * @method changeActionStates - change value to multiple actions at once, after which event 'onChange' is triggered.
     * @param actionNames
     * @param value
     */
    public changeActionStates(actions: ActionsTypes): void;
    public changeActionStates(value: boolean, actionNames: Array<string>): void;
    public changeActionStates(actionsOrValue: boolean | ActionsTypes, actionNames?: Array<string>): void {
        this.beginBatchUpdates();

        try {
            var hasValue = typeof actionsOrValue === "boolean";
            if (hasValue) {
                for (var i = 0; i < actionNames.length; i++) {
                    var action = actionNames[i];
                    this.changeActionState(action, actionsOrValue as boolean);
                }
            } else {
                for (var key in actionsOrValue as ActionsTypes) {
                    if ((actionsOrValue as ActionsTypes).hasOwnProperty(key)) {
                        this.changeActionState(key, (actionsOrValue as ActionsTypes)[key]);
                    }
                }
            }
        } finally {
            this.endBatchUpdates(true);
        }
    }

    /**
     * Change value for specific action.
     * @method changeActionState
     * @param action string name of action
     * @param value boolean
     * @return true when state was successfully changed, otherwise returns false
     */
    public changeActionState(action: string, value: boolean): boolean {
        this.checkForReadonly();

        var exist = !Utils.isNullOrUndefined(this._actions[action]);

        if (exist) {
            this._actions[action] = value;

            this.invokeOnChange();
        } else {
            if (DEBUG) {
                this.log(
                    `[changeActionState] Could not change action '${action}' value to '${value}', action was not registered!` +
                        ` all actions: ${JSON.stringify(this._actions)}`,
                );
            }
        }

        return exist;
    }

    /**
     * Get action state (true - action is enabled, false - action is disabled)
     * @method getActionState
     * @param action string name of action
     * @return boolean
     */
    public getActionState(action: string): boolean {
        if (this._actions[action] != null) {
            return this._actions[action];
        } else {
            if (DEBUG) {
                this.log(
                    `[changeActionState] Could not get action '${action}' value, action was not registered!` +
                        ` all actions: ${JSON.stringify(this._actions)}`,
                );
            }
        }
        return false;
    }

    /**
     * Return all actions with states.
     * @method getAllActions
     * @return Object
     */
    public getAllActions(): ActionsTypes {
        return this._actions;
    }

    /**
     * clone action set to new instance and is returned as readonly by default
     */
    public clone(readonly?: boolean): ActionSet {
        var cloned = new ActionSet(this._setName, this._owner);

        for (var key in this._actions) {
            if (this._actions.hasOwnProperty(key)) {
                cloned.registerAction(key, this._actions[key]);
            }
        }

        cloned._readonly = Utils.valueOrDefault(readonly, true);
        return cloned;
    }

    /**
     * Add value false for all action. Use this method for example after dispatch spin action.
     * @method disableAllActions
     * @return void
     */
    public disableAllActions(): void {
        this.beginBatchUpdates();

        try {
            for (var key in this._actions) {
                this._actions[key] = false;
            }
        } finally {
            this.endBatchUpdates(true);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Actions/ActionSet.ts
 **/